import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { defineMessages, FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { observer } from 'mobx-react-lite';

import {
    CalculationLineBlock,
    CalculationPanel,
    CalculatorStepAccordion,
    FormComputed,
    useForm,
    MessagePropType,
    Paragraph,
    Question,
    Sizes,
    Colours,
    BOOLEAN_OPTIONS,
} from '@ratehub/base-ui';

import getCashNeededCalculations, {
    getCashNeeded,
} from '../functions/getCashNeededCalculations';
import { HOME_TYPE_OPTIONS } from '../definitions/AffordabilityMultiInputFields';


function AffordabilityStepCashNeeded({
    onToggleExpand,
    title,
    stepNumber,
    cashNeededIntroduction,
    ...otherProps
}) {
    const form = useForm();
    const { homeType } = form.values;

    const formulas = useMemo(
        () => getFormulas(homeType),
        [ homeType ],
    );

    return (
        <>
            <FormComputed
                name="cashNeeded"
                value={getCashNeeded}
            />

            <CalculatorStepAccordion
                title={title}
                stepNumber={stepNumber}
                onToggleExpand={onToggleExpand}
                showBadge={false}
                value="cashNeeded"
                valueFormat="CAD0"
                {...otherProps}
            >
                <Paragraph
                    message={
                        cashNeededIntroduction || MESSAGES.cashIntroduction
                    }
                    className="introduction"
                />
                <div className="question-flex-box">
                    <div className="question">
                        <Question
                            field={{
                                name: 'isFirstTimeBuyer',
                                type: 'boolean',
                                value: true,
                                options: BOOLEAN_OPTIONS,
                            }}
                            type="radio"
                            labelMessage={MESSAGES.isFirstTimeBuyer}
                            className="question-no-margin"
                            flowDirection="row"
                        />
                    </div>
                    <div className="question">
                        <Question
                            field={{
                                name: 'homeType',
                                type: 'choice',
                                options: HOME_TYPE_OPTIONS,
                                value: 'house',
                            }}
                            labelMessage={MESSAGES.typeOfHouse}
                            className="question-no-margin"
                            flowDirection="row"
                        />
                    </div>
                </div>

                <CalculationPanel
                    title={(
                        <FormattedMessage
                            id="mtg-calculators.AffordabilityStepCashNeeded.calculationPanelTitle"
                            defaultMessage="Required cash expenditures"
                        />
                    )}
                >
                    <CalculationLineBlock formulas={formulas} />
                </CalculationPanel>
            </CalculatorStepAccordion>
        </>
    );
}

AffordabilityStepCashNeeded.propTypes = {
    stepNumber: PropTypes.number.isRequired,
    cashNeededIntroduction: MessagePropType,
    title: MessagePropType.isRequired,
    onToggleExpand: PropTypes.func,
};

AffordabilityStepCashNeeded.defaultProps = {
    cashNeededIntroduction: undefined,
    onToggleExpand: undefined,
};

const MESSAGES = defineMessages({
    cashIntroduction: {
        id: 'mtg-calculators.AffordabilityCashNeeded.cashNeededInformation',
        defaultMessage:
            'When you purchase a house, there are a number of costs you will need to put aside in addition to your down payment.',
    },
    isFirstTimeBuyer: {
        id: 'mtg-calculators.AffordabilityCashNeeded.isFirstTimeBuyerLabel',
        defaultMessage: 'Are you a first time buyer',
    },
    typeOfHouse: {
        id: 'mtg-calculators.AffordabilityCashNeeded.typeOfHouseLabel',
        defaultMessage: 'Type of house',
    },
});

function getFormulas(typeOfHouse) {
    const calculations = getCashNeededCalculations({
        homePriceName: 'maxAffordability',
        downPaymentDollarsName: 'estimatedDownPayment',
        cmhcCalculationName: 'cmhcCalculation',
        homeLocationName: 'futureHomeLocation',
        isFirstTimeBuyerName: 'isFirstTimeBuyer',
    });

    if (typeOfHouse == 'condo') {
        return [
            calculations.downPaymentDollarCalculation,
            calculations.PSTCalculation,
            calculations.landTransferCalculation,
            calculations.lawyerFeeCalculation,
            calculations.titleInsuranceCalculation,
            calculations.homeInspectionCalculation,
            calculations.appraisalCalculation,
            calculations.estoppelFeeCalculation,
            calculations.cashRequiredCalculation,
        ];
    }

    return [
        calculations.downPaymentDollarCalculation,
        calculations.PSTCalculation,
        calculations.landTransferCalculation,
        calculations.lawyerFeeCalculation,
        calculations.titleInsuranceCalculation,
        calculations.homeInspectionCalculation,
        calculations.appraisalCalculation,
        calculations.cashRequiredCalculation,
    ];
}

export default styled(observer(AffordabilityStepCashNeeded))`
    .question-flex-box {
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        justify-content: space-between;

        column-gap: ${Sizes.SPACING.THREE};

        margin-top: ${Sizes.SPACING.THREE};

        .question {
            flex: 200px 1 0;

            .question-no-margin {
                margin-top: 0;
                margin-bottom: ${Sizes.SPACING.THREE};
            }
        }
    }

    .scenarioChooser {
        border-bottom: 1px solid ${Colours.STONE};
    }

    .introduction {
        text-align: justify;
        margin: 0;
    }
`;
